<script lang="ts">
  import autoAnimate from "@formkit/auto-animate";
  import Dropdown from "$components/+blocks/Dropdown/Dropdown.svelte";
  import IconButtonTrigger from "$components/+blocks/Dropdown/fields/IconButtonTrigger.svelte";
  import IconButton from "$components/+blocks/IconButton/IconButton.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Search from "$components/+blocks/Dropdown/fields/Search.svelte";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import OptionGroup from "$components/+blocks/Dropdown/fields/OptionGroup.svelte";
  import CheckboxOption from "$components/+blocks/Dropdown/fields/CheckboxOption.svelte";
  import { createEventDispatcher } from "svelte";
  import { tooltip } from "$actions/tooltip";
  import { labelsList } from "app/stores/labels";
  import { Cross, Dot, LockSolid, Label } from "$components/+blocks/OVIcon";
  import type { LabelSerialiser } from "@outverse-video/outverse-api-client-lib";

  export let selectedLabels: LabelSerialiser[];
  export let canEdit: boolean;

  const MAX_LABELS = 6;
  const dispatch = createEventDispatcher<{ change: LabelSerialiser[] }>();
</script>

<div class="w-full overflow-hidden">
  <Dropdown maxWidth={300} fullWidth>
    <div
      let:active
      slot="trigger"
      use:autoAnimate={{ duration: 100 }}
      class="flex w-full flex-wrap items-start gap-1 rounded-lg"
    >
      {#if canEdit}
        <IconButtonTrigger {active} a11yLabel="Labels" variant="secondary" icon={Label} />
      {/if}
      {#if selectedLabels?.length}
        {#each selectedLabels.slice(0, MAX_LABELS) as label (label.uuid)}
          <div
            class="flex min-w-0 items-center gap-1.5 rounded-[5px] border-gray-300 bg-black/5 py-1 pr-1 pl-1.5 font-medium dark:border-white/10 dark:bg-white/5"
          >
            <div class="flex items-center gap-1 overflow-hidden whitespace-nowrap">
              <span style:color={label.color}>
                <OVIcon icon={Dot} size="w-3.5" />
              </span>
              <span class="truncate text-xs text-gray-500 dark:text-gray-300">{label.name}</span>
              {#if label.teamOnly}
                <span
                  use:tooltip
                  title="Only team members can see this label"
                  class="text-gray-500 dark:text-gray-400"
                >
                  <OVIcon icon={LockSolid} />
                </span>
              {/if}
            </div>
            {#if canEdit}
              <IconButton
                icon={Cross}
                a11yLabel="Remove"
                variant="tertiary"
                size="small"
                customIconSize="h-3"
                on:click={(e) => {
                  e.stopPropagation();
                  selectedLabels = selectedLabels?.filter((f) => f.uuid != label.uuid);
                  dispatch("change", selectedLabels);
                }}
              />
            {/if}
          </div>
        {/each}

        {#if selectedLabels.length > MAX_LABELS}
          <span
            use:tooltip
            title={selectedLabels
              .slice(6)
              .map((label) => label.name)
              .join(", ")}
            class="rounded bg-black/5 px-2 py-1.5 text-xs font-medium text-gray-500 dark:bg-white/5 dark:text-gray-400"
          >
            +{selectedLabels.length - MAX_LABELS}
          </span>
        {/if}
      {/if}
    </div>
    <Search placeholder="Add labels..." />
    <Separator class="w-full" />

    <OptionGroup
      bind:values={selectedLabels}
      allowEmpty
      on:select={() => dispatch("change", selectedLabels)}
    >
      {#each $labelsList as label}
        <CheckboxOption
          title={label.name}
          icon={label.teamOnly ? LockSolid : Dot}
          value={label}
          iconColor={label.teamOnly ? "text-gray-500 dark:text-gray-400" : label.color}
        />
      {/each}
    </OptionGroup>
  </Dropdown>
</div>
